import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ImmobilieUebernehmenPayload,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  App,
  BasisAdresse,
  isBasisAdresseExist,
  mapVorbelegungdaten,
  VhvVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import {
  SpartenType,
  VertragsdatenRequestModel,
  VertragsVorbelegungdaten,
  VhvVorvertragsdaten,
} from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { equalAdressen } from '@ps20-frontend/vorschlagsdaten-lib';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';

export enum VorbelegungVhvRequestEventsEnum {
  VORBELEGUNG_VHV_REQUEST_SUCCESSFULL = 'VORBELEGUNG_VHV_REQUEST_SUCCESSFULL',
  VORBELEGUNG_VHV_REQUEST_FAILED = 'VORBELEGUNG_VHV_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.VHV));
  yield takeEvery(
    VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.VHV),
  );
  yield takeEvery(
    VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_FAILED,
    safeSaga(errorCallback, App.VHV),
  );
  yield takeEvery(GlobalActions.VORBELEGUNG_FINISHED, safeSaga(zusatzoptionenVorbelegung, App.VHV));
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>) {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.VHV || action.payload.angebotsId !== state.angebotsId) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* vhvDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.VHV,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<VhvVorvertragsdaten>) {
  const vhvVorbelegungdaten: VhvVorbelegungdaten = getVHVVorbelegungdaten(successAction.payload);
  yield* vhvDatenVorbelegen(vhvVorbelegungdaten);
}

const vhvActions = new VhvActions();

export function* vhvDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();
  let beibehalten = false;
  const vhvVorbelegungdaten: VhvVorbelegungdaten = vorbelegungdaten as VhvVorbelegungdaten;
  const globalPut = GlobalPut.getInstance();

  yield put(vhvActions.familienstandChanged(vhvVorbelegungdaten.familienstand));

  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.VHV,
        state.angebotsId,
      ),
    );
  }
  if (state.vertragsoptionen?.beibehalten) {
    vhvVorbelegungdaten.angebotsId = state.angebotsId;
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(vhvVorbelegungdaten),
    );
    if (!vhvVorbelegungdaten.wertsachensumme) {
      yield take(VhvActions.WERTSACHENSUMMEN_CHANGED_AFTER_VORBELEGUNG);
    }
    beibehalten = true;
  }
  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.VHV,
      vorhandeneVorbelegungdaten: vhvVorbelegungdaten,
      angebotsId: state.angebotsId,
      beibehalten,
    }),
  );
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.VHV }));
  yield cancel();
}

export function* zusatzoptionenVorbelegung(action: GlobalAction<VorbelegungPayload>) {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.VHV || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  if (!action.payload.beibehalten) {
    let selectedRisikoort: BasisAdresse;

    if (
      state.vorbelegungen.adresseImmobilieVorbelegt === false ||
      !isBasisAdresseExist(state.risikoort as BasisAdresse)
    ) {
      const immobilieAction: GlobalAction<ImmobilieUebernehmenPayload> = yield take(
        GlobalActions.IMMOBILIE_UEBERNEHMEN,
      );
      selectedRisikoort = immobilieAction.payload.immobilie?.risikoort as BasisAdresse;
    } else {
      selectedRisikoort = state.risikoort as BasisAdresse;
    }

    const vhvVorbelegungdaten: VhvVorbelegungdaten =
      action.payload.vorhandeneVorbelegungdaten as VhvVorbelegungdaten;
    if (equalAdressen(selectedRisikoort, vhvVorbelegungdaten.risikoort as BasisAdresse)) {
      yield put(vhvActions.produktZusatzoptionenVorbelegt(vhvVorbelegungdaten));
    }
  }
}

function getVHVVorbelegungdaten(
  bffVorbelegungdaten: VertragsVorbelegungdaten,
): VhvVorbelegungdaten {
  return mapVorbelegungdaten(SpartenType.HAUSRAT, bffVorbelegungdaten) as VhvVorbelegungdaten;
}
